import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Clock, Eye, ChevronLeft, Share2, Tag } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from '../LanguageContext';

const AVATAR_MAP = {
  hipo_thumbsup: '/hipo_thumbsup.webp',
  hipo_thinking: '/hipo_thinking.webp',
  hipo_checkmark: '/hipo_checkmark.webp',
  hipo_laptop: '/hipo_laptop.webp',
  hipo_sad: '/hipo_sad.webp',
  hipo_heart: '/hipo_heart.webp',
  hipo_sunglasses: '/hipo_sunglasses.webp',
};

const CATEGORY_COLORS = {
  Future: 'bg-blue-500/15 text-blue-400',
  Money: 'bg-green-500/15 text-green-400',
  System: 'bg-red-500/15 text-red-400',
  Power: 'bg-purple-500/15 text-purple-400',
  Style: 'bg-pink-500/15 text-pink-400',
  Mind: 'bg-amber-500/15 text-amber-400',
  'AI Insights': 'bg-cyan-500/15 text-cyan-400',
};

const ArticlePage = () => {
  const { t } = useTranslation();
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/magazine/articles/${slug}`)
      .then(r => setArticle(r.data))
      .catch(() => toast.error(t('mag.articleNotFound')))
      .finally(() => setLoading(false));
  }, [slug, t]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!article) return (
    <div className="min-h-screen flex flex-col items-center justify-center">
      <img src="/hipo_sad.webp" alt="" className="w-20 h-20 mb-4" />
      <h1 className="font-heading text-2xl font-bold mb-2">{t('mag.articleNotFound')}</h1>
      <Link to="/magazine" className="text-cyan-500 text-sm hover:underline">{t('mag.backToMag')}</Link>
    </div>
  );

  const avatar = AVATAR_MAP[article.avatar] || AVATAR_MAP.hipo_thumbsup;
  const catColor = CATEGORY_COLORS[article.category] || '';

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success(t('common.linkCopied'));
  };

  return (
    <div className="min-h-screen py-8 sm:py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {/* Back */}
        <Link to="/magazine" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-cyan-400 mb-6 transition-colors">
          <ChevronLeft className="w-3.5 h-3.5" /> {t('mag.backToMag')}
        </Link>

        {/* Category */}
        <span className={`inline-block px-3 py-1 rounded-md text-[10px] font-semibold uppercase tracking-wider mb-4 ${catColor}`}>
          {article.category}
        </span>

        {/* Title */}
        <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl font-bold leading-tight mb-4">
          {article.title}
        </h1>

        {/* Meta */}
        <div className="flex items-center gap-4 mb-8 flex-wrap">
          <div className="flex items-center gap-2">
            <img src={avatar} alt="Hipo Writer" className="w-9 h-9" />
            <div>
              <p className="text-xs font-semibold">{t('mag.hipoWriter')}</p>
              <p className="text-[10px] text-gray-500">{new Date(article.published_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-3 text-[10px] text-gray-500 ml-auto">
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.read_time_minutes} {t('mag.minRead')}</span>
            <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {article.views} {t('mag.views')}</span>
            <button onClick={handleShare} className="flex items-center gap-1 hover:text-cyan-400 transition-colors"><Share2 className="w-3 h-3" /> {t('mag.share')}</button>
          </div>
        </div>

        {/* Article Content */}
        <article className="prose prose-invert prose-sm max-w-none 
          prose-headings:font-heading prose-headings:text-white prose-headings:font-bold
          prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-3
          prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-2
          prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-4
          prose-a:text-cyan-500 prose-a:no-underline hover:prose-a:underline
          prose-strong:text-white
          prose-ul:text-gray-300 prose-ol:text-gray-300
          prose-blockquote:border-cyan-500/30 prose-blockquote:text-gray-400
        ">
          <ReactMarkdown>{article.content}</ReactMarkdown>
        </article>

        {/* Tags */}
        {article.tags && article.tags.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap mt-10 pt-6 border-t border-white/5">
            <Tag className="w-3.5 h-3.5 text-gray-600" />
            {article.tags.map(tag => (
              <span key={tag} className="px-2.5 py-1 rounded-md bg-white/[0.03] text-[10px] text-gray-400 border border-white/5">
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="mt-10 p-6 rounded-2xl bg-cyan-500/5 border border-cyan-500/15 text-center">
          <img src="/hipo_checkmark.webp" alt="" className="w-12 h-12 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold mb-1">{t('mag.ctaTitle')}</h3>
          <p className="text-gray-500 text-xs mb-4">{t('mag.ctaDesc')}</p>
          <Link to="/cars">
            <button className="px-6 py-2.5 bg-cyan-500 text-black rounded-lg font-bold text-sm hover:bg-cyan-400 transition-colors">
              {t('mag.browseCars')}
            </button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ArticlePage;
